'use client';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import styles from './Accordion.module.scss';

export interface AccordionItemProps {
  children: ReactNode;
  isOpen?: boolean;
  onClick?: () => void;
  title: string;
  start?: ReactNode;
  end?: ReactNode;
  arrow?: 'start' | 'end';
}

function debounce<T extends (...args: any[]) => void>(
  func: T,
  delay: number,
): (...args: Parameters<T>) => void {
  let timeoutId: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
}

const AccordionItem: React.FC<AccordionItemProps> = ({
  children,
  isOpen = false,
  onClick,
  title,
  start,
  end,
  arrow = 'end',
  ...props
}) => {
  const [maxHeight, setMaxHeight] = useState<string>('0px');
  const contentRef = useRef<HTMLDivElement>(null);
  let itemContainer = `${styles.item}${isOpen ? ' ' + styles.expanded : ''}`;
  if (arrow) itemContainer += ` ${styles[arrow]}`;

  useEffect(() => {
    if (!isOpen && contentRef.current) {
      setMaxHeight('0px');
    }
    const handleResize = () => {
      if (isOpen && contentRef.current) {
        // console.log('resizing....');
        setMaxHeight(`${contentRef.current.scrollHeight}px`);
      }
    };

    const debouncedHandleResize = debounce(handleResize, 300);
    window.addEventListener('resize', debouncedHandleResize);
    handleResize();
    return () => window.removeEventListener('resize', debouncedHandleResize);
  }, [isOpen]);

  return (
    <div className={itemContainer} {...props}>
      <div className={styles.header} role="tab">
        {start && <div>{start}</div>}
        <button className={styles.button} onClick={onClick}>
          <span className={styles.buttonText}>{title}</span>
        </button>
        {end && <div>{end}</div>}
      </div>
      <div ref={contentRef} className={styles.body} role="tabpanel" style={{ maxHeight }}>
        {children}
      </div>
    </div>
  );
};

export default AccordionItem;
